import React, { useState, useEffect } from "react";
import { db, storage } from "../../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { FiSave, FiUpload } from "react-icons/fi";
import { compressImage } from "../../utils/imageUtils";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import toast from "react-hot-toast";

export default function AdminAbout() {
    const [form, setForm] = useState({
        heading: "", bio: "", photoUrl: "", instagram: "", facebook: "", tiktok: "", goodreads: ""
    });
    const [loading, setLoading] = useState(true);
    console.log("AdminAbout: Component rendering, loading =", loading);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    const [genConfig, setGenConfig] = useState(null);

    useEffect(() => {
        console.log("AdminAbout: useEffect mounting");
        async function load() {
            try {
                console.log("AdminAbout: Fetching siteConfig/about and general");
                const [aboutSnap, genSnap] = await Promise.all([
                    getDoc(doc(db, "siteConfig", "about")),
                    getDoc(doc(db, "siteConfig", "general"))
                ]);
                
                if (aboutSnap.exists()) {
                    const data = aboutSnap.data();
                    console.log("AdminAbout: Found about data", data);
                    setForm(prev => ({
                        ...prev,
                        ...data,
                        heading: data.heading || "",
                        bio: data.bio || "",
                        photoUrl: data.photoUrl || "",
                        instagram: data.instagram || "",
                        facebook: data.facebook || "",
                        tiktok: data.tiktok || "",
                        goodreads: data.goodreads || ""
                    }));
                } else {
                    console.log("AdminAbout: No about data found in siteConfig/about");
                }
                if (genSnap.exists()) {
                    console.log("AdminAbout: Found general config", genSnap.data());
                    setGenConfig(genSnap.data());
                }
            } catch (err) {
                console.error("AdminAbout: Error loading about page data:", err);
                toast.error("Could not load about page.");
            } finally {
                console.log("AdminAbout: Setting loading to false");
                setLoading(false);
            }
        }
        load();
    }, []);

    function handleChange(e) {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
    }

    async function handlePhotoUpload(e) {
        let file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            file = await compressImage(file);
            const storageRef = ref(storage, `about/${Date.now()}_${file.name}`);
            const task = uploadBytesResumable(storageRef, file);
            task.on("state_changed", null, (err) => { 
                console.error(err);
                toast.error("Upload failed."); 
                setUploading(false); 
            }, async () => {
                const url = await getDownloadURL(task.snapshot.ref);
                setForm(f => ({ ...f, photoUrl: url }));
                setUploading(false);
                toast.success("Photo uploaded!");
            });
        } catch (e) {
            toast.error("Compression failed.");
            setUploading(false);
        }
    }

    async function save() {
        setSaving(true);
        try {
            await setDoc(doc(db, "siteConfig", "about"), form);
            toast.success("About page saved!");
        } catch {
            toast.error("Failed to save.");
        }
        setSaving(false);
    }

    if (loading) return <div className="admin-loading">Loading About Page Data…</div>;

    try {
        return (
            <div className="admin-about-section">
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 24 }}>
                    <button className="admin-btn-primary" onClick={save} disabled={saving || uploading}>
                        <FiSave size={16} /> {saving ? "Saving…" : "Save Changes"}
                    </button>
                </div>

                <div className="admin-card" style={{ padding: 28 }}>
                    <div className="admin-form">
                        <div className="admin-form-grid">
                            <div className="admin-form-field">
                                <label>Section Heading</label>
                                <input 
                                    name="heading" 
                                    value={form.heading || ""} 
                                    onChange={handleChange} 
                                    placeholder={`About ${genConfig?.authorName || "Liliz Black"}`} 
                                />
                            </div>
                            <div className="admin-form-field">
                                <label>Biography</label>
                                <div className="quill-editor-wrapper" style={{ background: "white", borderRadius: 8 }}>
                                    <ReactQuill 
                                        theme="snow"
                                        value={form.bio || ""}
                                        onChange={(content) => setForm(f => ({ ...f, bio: content }))}
                                        modules={{
                                            toolbar: [
                                                ['bold', 'italic', 'underline', 'strike'],
                                                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                                ['clean']
                                            ],
                                        }}
                                        placeholder={`${genConfig?.authorName || "Liliz Black"} is a romance author from San Juan, Puerto Rico…`}
                                    />
                                </div>
                            </div>
                            <div className="admin-form-field full">
                                <label>Author Photo</label>
                                {form.photoUrl && (
                                    <img 
                                        src={form.photoUrl} 
                                        alt="Author" 
                                        style={{ height: 160, width: 160, objectFit: "cover", borderRadius: 10, marginBottom: 10 }} 
                                    />
                                )}
                                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                    <label className="admin-upload-label" style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", background: "#f4f5f7", border: "1.5px dashed #ccc", padding: "10px 16px", borderRadius: 8, fontWeight: 600, fontSize: 13, color: "#555", width: "fit-content" }}>
                                        <FiUpload size={16} />
                                        {uploading ? "Uploading…" : "Upload Photo"}
                                        <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: "none" }} />
                                    </label>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                        <span style={{ fontSize: 12, color: "#888" }}>Or paste URL:</span>
                                        <input 
                                            name="photoUrl" 
                                            value={form.photoUrl || ""} 
                                            onChange={handleChange} 
                                            placeholder="https://..." 
                                        />
                                    </div>
                                </div>
                            </div>

                            <div style={{ gridColumn: "1 / -1", marginTop: 10 }}>
                                <p style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "#888", letterSpacing: 0.5, marginBottom: 16 }}>Social Links</p>
                            </div>

                            <div className="admin-form-field">
                                <label>Instagram URL</label>
                                <input name="instagram" value={form.instagram || ""} onChange={handleChange} placeholder="https://instagram.com/lilizblack/" />
                            </div>
                            <div className="admin-form-field">
                                <label>Facebook URL</label>
                                <input name="facebook" value={form.facebook || ""} onChange={handleChange} placeholder="https://facebook.com/LBLilizBlack" />
                            </div>
                            <div className="admin-form-field">
                                <label>TikTok URL</label>
                                <input name="tiktok" value={form.tiktok || ""} onChange={handleChange} placeholder="https://tiktok.com/@liliz.black" />
                            </div>
                            <div className="admin-form-field">
                                <label>Goodreads URL</label>
                                <input name="goodreads" value={form.goodreads || ""} onChange={handleChange} placeholder="https://www.goodreads.com/author/..." />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    } catch (e) {
        console.error("AdminAbout: Render crash!", e);
        return <div style={{ padding: 40, color: "red" }}>Error rendering About page editor. Check console.</div>;
    }
}
